import { MapEvent } from "react-map-gl/maplibre";

import { Bounds, XYLocation } from "@nens/deck-gl";

import { AppState, Thunk } from "../App";

import { BACK_TO_START_SCREEN, BackToStartScreen } from "./appState";
import { Websocket, WebsocketStatus, getWebsockets, socketSend } from "./websockets";
type Map = MapEvent["target"];
interface MapState {
  map: Map | null;
  mouseLocation: XYLocation | null;
  zoom: number | null;
  bounds: Bounds | null;
  blockClick: string | null;
  setView: {
    location: XYLocation | null;
    number: number;
    zoom: number;
  };
  hoverCircleCoordinates: [number, number] | null;
}

const INITIAL_STATE: MapState = {
  map: null,
  mouseLocation: null,
  zoom: null,
  bounds: null,
  blockClick: null,
  setView: {
    location: null,
    number: 0,
    zoom: 0,
  },
  hoverCircleCoordinates: null,
};

// Action types

const SET_MAP = "map/SET_MAP";
const SET_MOUSE_LOCATION = "map/SET_MOUSE_LOCATION";
const SET_BOUNDS_AND_ZOOM = "map/SET_BOUNDS_AND_ZOOM";
const SET_BLOCK_CLICK = "map/SET_BLOCK_CLICK";
const SET_VIEW_LOCATION_AND_ZOOM_STATE = "map/SET_VIEW_LOCATION_AND_ZOOM_STATE";
const SET_HOVER_CIRCLE_COORDINATES = "map/SET_HOVER_CIRCLE_COORDINATES";
interface SetViewLocationAndZoomState {
  type: typeof SET_VIEW_LOCATION_AND_ZOOM_STATE;
  location: XYLocation;
  zoom: number;
}
interface SetMouseLocation {
  type: typeof SET_MOUSE_LOCATION;
  mouseLocation: XYLocation;
}
interface SetMap {
  type: typeof SET_MAP;
  map: Map;
}
interface SetBoundsAndZoom {
  type: typeof SET_BOUNDS_AND_ZOOM;
  bounds: Bounds;
  zoom: number;
}
interface SetBlockClick {
  type: typeof SET_BLOCK_CLICK;
  blockClick: string | null;
}
interface SetHoverCircleCoordinates {
  type: typeof SET_HOVER_CIRCLE_COORDINATES;
  coordinates: [number, number] | null;
}

type MapAction =
  | SetMap
  | SetMouseLocation
  | SetBoundsAndZoom
  | SetViewLocationAndZoomState
  | SetBlockClick
  | SetHoverCircleCoordinates
  | BackToStartScreen;

// Reducer

export default function reducer(state = INITIAL_STATE, action: MapAction): MapState {
  switch (action.type) {
    case BACK_TO_START_SCREEN:
      return INITIAL_STATE;
    case SET_HOVER_CIRCLE_COORDINATES:
      return {
        ...state,
        hoverCircleCoordinates: action.coordinates,
      };
    case SET_VIEW_LOCATION_AND_ZOOM_STATE:
      return {
        ...state,
        setView: {
          location: action.location,
          zoom: action.zoom,
          number: state.setView.number + 1,
        },
      };
    case SET_MOUSE_LOCATION:
      return {
        ...state,
        mouseLocation: action.mouseLocation,
      };
    case SET_MAP:
      return {
        ...state,
        map: action.map,
        zoom: action.map.getZoom(),
        bounds: caluclateMapBounds(action.map),
      };
    case SET_BOUNDS_AND_ZOOM:
      return {
        ...state,
        bounds: action.bounds,
        zoom: action.zoom,
      };
    case SET_BLOCK_CLICK:
      return {
        ...state,
        blockClick: action.blockClick,
      };
    default:
      return state;
  }
}

// Action creators

export const setViewLocationAndZoom = (location: XYLocation | null, zoom: number) => {
  return {
    type: SET_VIEW_LOCATION_AND_ZOOM_STATE,
    location,
    zoom,
  };
};

export const setMap = (map: Map): SetMap => {
  return {
    type: SET_MAP,
    map,
  };
};

export const setMouseLocation = (mouseLocation: XYLocation): SetMouseLocation => {
  return {
    type: SET_MOUSE_LOCATION,
    mouseLocation,
  };
};

export const setBoundsAndZoom = (bounds: Bounds, zoom: number): SetBoundsAndZoom => {
  return {
    type: SET_BOUNDS_AND_ZOOM,
    bounds,
    zoom,
  };
};

export const setBlockClick = (blockClick: string | null): SetBlockClick => {
  return {
    type: SET_BLOCK_CLICK,
    blockClick,
  };
};

export const updateBoundsAndZoom = (): Thunk => (dispatch, getState) => {
  // If the map bounds and/or zoom level have changed, we update them
  // both in Redux and in the backend, over the Websocket.

  const state = getState();
  const map = getMap(state);
  if (!map) return;
  const bounds = caluclateMapBounds(map);
  const zoom = map.getZoom();
  dispatch(setBoundsAndZoom(bounds, zoom));
  // Update bounds and image size in the backend.
  if (getWebsockets(state)[Websocket.Waterdepths].status === WebsocketStatus.Open) {
    const socketSend = sendZoomAndBoundsToWebsocket(map);
    if (socketSend) {
      dispatch(socketSend);
    }
  }
};

const epsg4326toEpsg3857 = (coordinates: XYLocation) => {
  const x = (coordinates[0] * 20037508.34) / 180;
  let y = Math.log(Math.tan(((90 + coordinates[1]) * Math.PI) / 360)) / (Math.PI / 180);
  y = (y * 20037508.34) / 180;
  return [x, y];
};

const caluclateMapBounds = (map: Map): [number, number, number, number] => {
  const mapBounds = map.getBounds();
  const nw = mapBounds.getNorthWest();
  const se = mapBounds.getSouthEast();
  return [nw.lng, nw.lat, se.lng, se.lat];
};

export const sendZoomAndBoundsToWebsocket = (map: Map) => {
  const mapbounds = caluclateMapBounds(map);
  const nw3857 = epsg4326toEpsg3857([mapbounds[0], mapbounds[1]]);
  const se3857 = epsg4326toEpsg3857([mapbounds[2], mapbounds[3]]);
  const canvas = map.getCanvas();
  const params = {
    width: canvas.clientWidth,
    height: canvas.clientHeight,
    crs: "EPSG3857",
    bbox: [nw3857[0], se3857[1], se3857[0], nw3857[1]],
  };

  if (params.width === 0 || params.height === 0) {
    return; // The map hasn't been sufficiently initialized yet
  }

  return socketSend(params, Websocket.Waterdepths);
};

export const setHoverCircleCoordinates = (coordinates: [number, number] | null) => {
  return {
    type: SET_HOVER_CIRCLE_COORDINATES,
    coordinates,
  };
};

// Selectors
export const getBlockClick = (state: AppState) => state.map.blockClick;

export const getMouseLocation = (state: AppState) => state.map.mouseLocation;

export const getMap = (state: AppState) => state.map.map;

export const getZoom = (state: AppState) => state.map.zoom;

export const getSetView = (state: AppState) => state.map.setView;

export const getMapBounds = (state: AppState) => state.map.bounds;

export const getHoverCircleCoordinates = (state: AppState) => state.map.hoverCircleCoordinates;

export const getOverlaps = (bounds1: Bounds, bounds2: Bounds) =>
  Math.min(bounds1[2], bounds2[2]) > Math.max(bounds1[0], bounds2[0]) &&
  Math.min(bounds1[1], bounds2[1]) > Math.max(bounds1[3], bounds2[3]);
